import React, { useState, type ReactElement } from "react";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import "react-datepicker/dist/react-datepicker.css";
import "./DateInput.scss";
import IconButton from "../Buttons/IconButton/IconButton.js";
import TextInput from "../TextInput/TextInput.js";
import { SeemPopover, openManagementModesEnum } from "../../components";
import {
  dateFormatType,
  getFormattedDate,
  parseToDateObj,
  getFormattedTime,
  getDataAnalyticsProps,
} from "../../../utils";
import classNames from "classnames";

export interface DateInputChangeEvent {
  target: {
    name: string;
    value: string;
  };
}

export interface DateInputProps {
  "data-testid"?: string;
  wrapperClassName?: string;
  className?: string;
  popoverClassName?: string;
  label?: string;
  labelTop?: boolean;
  noMargin?: boolean;
  inputRef?: React.Ref<HTMLInputElement> | null;
  withHelp?: string;
  name?: string;
  error?: string | null;
  disabled?: boolean;
  readOnly?: boolean;
  value?: string | null;
  size?: string;
  dateFormat?: string;
  timeFormat?: string;
  maxDate?: string | null;
  minDate?: string | null;
  isClearable?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  showTimeSelect?: boolean;
  timeIntervals?: number;
  autoOpen?: boolean;
  includeTimes?: Date[];
  placeholder?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: DateInputChangeEvent) => void;
  popoverButton?: ReactElement | null;
}

export const defaultProps: DateInputProps = {
  "data-testid": "",
  wrapperClassName: "",
  label: "",
  labelTop: false,
  inputRef: null,
  withHelp: "",
  name: "",
  error: null,
  disabled: false,
  readOnly: false,
  value: null,
  size: "",
  dateFormat: "MMM dd, yyyy",
  timeFormat: "HH:mm",
  maxDate: null,
  minDate: null,
  isClearable: true,
  required: false,
  hideLabelHelper: false,
  showTimeSelect: false,
  timeIntervals: 60,
  placeholder: "",
  onBlur: () => {},
  onFocus: () => {},
  onChange: () => {},
  popoverButton: null,
};

const formatWeekDay = (day: string): string => {
  return day[0]; // Return only the first letter of the day
};

const formatDate = (
  date: string | Date | null | undefined,
  showTimeSelect?: boolean
): string =>
  date
    ? showTimeSelect
      ? getFormattedTime(date as string | number | Date)
      : getFormattedDate(date as string, dateFormatType.SHORT)
    : "";

const DateInput: React.FC<DateInputProps> = (props) => {
  const {
    "data-testid": dataTestId,
    label,
    noMargin,
    labelTop,
    required,
    showTimeSelect = false,
    hideLabelHelper,
    withHelp,
    name,
    wrapperClassName,
    error,
    disabled = false,
    readOnly = false,
    value,
    dateFormat = "MMM dd yyyy",
    timeFormat = "HH:mm",
    maxDate = null,
    minDate = null,
    isClearable = true,
    timeIntervals = 60,
    autoOpen = false,
    placeholder = "",
    onChange,
    onBlur,
    onFocus,
    includeTimes,
    popoverClassName,
    popoverButton,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(autoOpen);

  const handleDateChange = (date: Date | null) => {
    const formattedValue = !!date
      ? showTimeSelect
        ? formatDate(date, showTimeSelect)
        : new Date(date.getTime() - date.getTimezoneOffset() * 60000) // since the datepicker returns the date in UTC, we need to convert it to local time +the offset so that the date won't change
            .toISOString()
            .split("T")[0]
      : "";

    onChange?.({
      target: {
        name: name ?? "",
        value: formattedValue,
      },
    });
  };

  const getSelectedDate = (date: string | null | undefined): Date | null => {
    if (!!value) {
      const jsDate = showTimeSelect
        ? new Date(date as string)
        : (parseToDateObj(date as string) as Date);
      const dateTime = DateTime.fromJSDate(jsDate);

      if (!dateTime.isValid) {
        return null;
      }

      return jsDate;
    }

    return null;
  };

  const classNameWrapper = classNames(
    "input-wrap",
    "input-wrap-date",
    "no-padding-bottom",
    wrapperClassName,
    {
      "has-label": !!label,
      "has-label-top": labelTop,
      "hide-label-helper": hideLabelHelper,
      "is-disabled": disabled,
      "has-read-only": readOnly,
      "is-clearable": isClearable && !disabled && !readOnly,
      "has-error": !!error,
      "no-margin": noMargin,
      "is-timepicker": showTimeSelect,
    },
    props.className
  );

  return (
    <div className={classNameWrapper} data-testid={`${dataTestId}-date-input`}>
      <SeemPopover
        isOpen={isOpen}
        className={popoverClassName}
        useSameWidth={showTimeSelect}
        placement={"bottom-start"}
        onOuterAction={() => {
          setTimeout(() => setIsOpen(false), 0);
        }}
        openManagementMode={openManagementModesEnum.CLICK}
        body={
          <div
            className={"date-picker-wrapper"}
            key="date-picker-wrapper"
            onClick={(e) => e.stopPropagation()}
          >
            <DatePicker
              inline
              includeTimes={includeTimes}
              selected={getSelectedDate(value)}
              formatWeekDay={formatWeekDay}
              onChange={(date: Date | null) => {
                setTimeout(() => setIsOpen(false), 0);
                // Non-range DatePicker: runtime value is always a single Date | null.
                // Annotating the param selects react-datepicker v7's default
                // (single-date) props variant instead of the selectsRange one.
                handleDateChange(date);
              }}
              showTimeSelect={showTimeSelect}
              showTimeSelectOnly={showTimeSelect}
              timeIntervals={timeIntervals}
              onBlur={onBlur}
              onFocus={onFocus}
              dateFormat={showTimeSelect ? timeFormat : dateFormat}
              timeFormat={timeFormat}
              // react-datepicker types want Date | undefined; runtime historically
              // passed null when unset — preserve via assertion.
              maxDate={
                (maxDate
                  ? DateTime.fromISO(maxDate).toJSDate()
                  : null) as Date | undefined
              }
              minDate={
                (minDate
                  ? DateTime.fromISO(minDate).toJSDate()
                  : null) as Date | undefined
              }
              disabled={disabled}
              // `use24hourTime` is not in react-datepicker v7's types but was
              // passed in the original JS; preserve runtime behavior via a bridge.
              {...({ use24hourTime: true } as Record<string, unknown>)}
              renderCustomHeader={({
                monthDate,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="dateinput-calendar-header">
                  <IconButton
                    className={"calendar-prev-month-btn"}
                    data-testid={`calendar-prev-month`}
                    isIcon={"seem-icon-left-arrow"}
                    type={`button`}
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    size={"xs"}
                  />
                  <span className={"dateinput-calendar-month-name"}>
                    {monthDate.toLocaleString("default", { month: "long" })}{" "}
                    {monthDate.getFullYear()}
                  </span>
                  <IconButton
                    className={"calendar-next-month-btn"}
                    data-testid={`calendar-next-month`}
                    isIcon={"seem-icon-right-arrow"}
                    type={`button`}
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    size={"xs"}
                  />
                </div>
              )}
            />
          </div>
        }
      >
        {!!popoverButton ? (
          React.cloneElement(popoverButton, {
            onClick: () => !disabled && !readOnly && setIsOpen(true),
          })
        ) : (
          <TextInput
            {...getDataAnalyticsProps(props)}
            inDatepicker
            data-testid={dataTestId}
            onClick={() => !disabled && !readOnly && setIsOpen(true)}
            noMargin={noMargin}
            hideLabelHelper={hideLabelHelper}
            isFocused={isOpen}
            rightIcon={
              !readOnly && showTimeSelect
                ? "seem-icon-in-time"
                : !readOnly
                  ? "seem-icon-schedule"
                  : null
            }
            isClearable={isClearable && !disabled && !readOnly}
            wrapperClassName={wrapperClassName}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            labelTop={labelTop}
            label={label}
            error={error}
            placeholder={
              placeholder
                ? placeholder
                : showTimeSelect
                  ? "Select a Time"
                  : "Select a Date"
            }
            withHelp={withHelp}
            value={
              readOnly
                ? !!value
                  ? formatDate(value, showTimeSelect)
                  : showTimeSelect
                    ? "HH:mm"
                    : "MMM dd, yyyy"
                : formatDate(value, showTimeSelect)
            }
            onClear={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleDateChange(null);
            }}
          />
        )}
      </SeemPopover>
    </div>
  );
};

DateInput.defaultProps = defaultProps;

export default DateInput;
